import type { Extension } from "@codemirror/state";
import { useQuery } from "@tanstack/react-query";
import type { LanguageId } from "@ide/shared";
import type { ImportedSnippetSet, ImportedTheme } from "@ide/shared/vscode-content";
import { get } from "@/lib/api";
import { snip, snippets } from "./language-plugins";

/**
 * Temas e snippets trazidos do VS Code/Cursor (ADR 0024): vêm da conta e valem em qualquer
 * computador. O tema ativo pinta o editor e a interface; os snippets entram no autocompletar.
 */
export const vscodeKeys = {
  content: ["vscode-extensions", "content"] as const,
  list: ["vscode-extensions"] as const,
};

export interface VsCodeContent {
  theme: (ImportedTheme & { extensionId: string }) | null;
  snippets: ImportedSnippetSet[];
}

export const useVsCodeContent = (enabled: boolean) =>
  useQuery({
    queryKey: vscodeKeys.content,
    queryFn: () => get<VsCodeContent>("/me/vscode-extensions/content"),
    enabled,
    staleTime: 5 * 60_000,
  });

/** Snippets importados no autocompletar, por linguagem. */
export function importedSnippets(sets: ImportedSnippetSet[]): Extension[] {
  return sets.map((s) =>
    snippets(
      s.languages as LanguageId[],
      s.items.flatMap((i) => {
        try {
          return [snip(i.prefix, i.label, i.body)];
        } catch {
          return []; // corpo que o editor não entende: fica de fora
        }
      }),
    ),
  );
}

export const VSCODE_CSS_KEY = "kompil.vscodeThemeCss";

/** Variáveis da interface a partir do tema (só cores #rrggbb: nada de CSS arbitrário). */
export function importedUiCss(t: ImportedTheme): string {
  const ok = (v: string) => (/^#[0-9a-f]{6}$/i.test(v) ? v : null);
  const vars: Array<[string, string | null]> = [
    ["--bg", ok(t.ui.bg)],
    ["--canvas", ok(t.ui.bg)],
    ["--surface", ok(t.ui.bgSubtle)],
    ["--bg-subtle", ok(t.ui.bgSubtle)],
    ["--bg-muted", ok(t.ui.bgMuted)],
    ["--bg-emphasis", ok(t.ui.bgEmphasis)],
    ["--border", ok(t.ui.border)],
    ["--border-strong", ok(t.ui.borderStrong)],
    ["--fg", ok(t.ui.fg)],
    ["--fg-muted", ok(t.ui.fgMuted)],
    ["--fg-subtle", ok(t.ui.fgSubtle)],
    ["--fg-inverse", ok(t.ui.fgInverse)],
    ["--glow", ok(t.ui.accent) ? `${t.ui.accent}4d` : null],
    // A cor de destaque do tema importado vira o destaque do Kompil (ao vivo, ações, não lidos).
    ["--accent", ok(t.ui.accent)],
  ];
  return `html.ui-theme-vscode{${vars
    .filter((v): v is [string, string] => Boolean(v[1]))
    .map(([k, v]) => `${k}:${v}`)
    .join(";")}}`;
}

/** Aplica (ou tira) o CSS do tema importado e guarda para o theme-init.js (sem piscar ao abrir). */
export function applyImportedUi(t: ImportedTheme | null) {
  const id = "kompil-vscode-theme";
  let el = document.getElementById(id) as HTMLStyleElement | null;
  if (!t) {
    el?.remove();
    try {
      localStorage.removeItem(VSCODE_CSS_KEY);
    } catch {
      // sem armazenamento
    }
    return;
  }
  const css = importedUiCss(t);
  if (!el) {
    el = document.createElement("style");
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = css;
  try {
    localStorage.setItem(VSCODE_CSS_KEY, css);
  } catch {
    // sem armazenamento: só não é lembrado entre visitas
  }
}
